import { Toasts } from 'components/ui';
import Achievements from 'components/ui/Achievements';
import BlockedArea from 'components/ui/BlockedArea';
import Deposit from 'components/ui/Deposit';
import Divider from 'components/ui/Divider';
import GeneralInformation from 'components/ui/GeneralInformation';
import { ProfileHeader } from 'components/ui/Header';
import { Rating } from 'components/ui/Rating';
import RiskManagement from 'components/ui/RiskManagement';
import SecurityAnalysis from 'components/ui/SecurityAnalysis';
import SkeletonProfile from 'components/ui/Skeleton/Profile';
import TravelHistory from 'components/ui/TravelHistory';
import TruckInformation from 'components/ui/TruckInformation';
import { useAppContext } from 'hooks/useAppContext';
import { useDeviceSize } from 'hooks/useDeviceSize';
import { useFetchProfileQueries } from 'hooks/useFetchProfileQueries';
import { useProfileSizeDetect } from 'hooks/useProfileSizeDetect';
import { useTruckerProps } from 'hooks/useTruckerProps';
import React, { FC, MutableRefObject, useEffect, useRef } from 'react';
import { ITruckerProfileProps } from 'types';
import LoadError from '../LoadError';
import * as Styled from './styled';

const Profile: FC<ITruckerProfileProps> = ({ data, onClose }) => {
  const {
    state: {
      profileSize: { isProfileSizeSmall },
    },
    dispatch,
  } = useAppContext();

  const wrapperRef: MutableRefObject<HTMLDivElement | null> = useRef(null);

  useProfileSizeDetect(wrapperRef);
  useDeviceSize(true);

  const {
    portalName,
    hideProfileToasts,
    onClose: onCloseProp,
    isOpenSearch,
    initialTruckerData,
    initialContactListFeatureData,
    showDepositInfo,
  } = useTruckerProps();

  const { isLoading, isFetched, isError, refetch, truckerData } = useFetchProfileQueries({
    truckerProfileOptions: {
      initialData: initialTruckerData || data?.initialTruckerData || undefined,
    },
    contactListFeatureOptions: {
      initialData: initialContactListFeatureData || data?.initialContactListFeatureData || undefined,
    },
  });

  useEffect(() => {
    if (data && onClose) {
      dispatch({ type: 'trucker_profile_props', payload: { ...data, onClose } });
    }
  }, []);

  const grStatus = truckerData?.freight?.deposit.grStatus;
  const proposalId = truckerData?.freight?.deposit.proposalId;
  const isPaidFee = showDepositInfo && !!proposalId;
  const hasAchievement = truckerData?.stamps?.position?.some((achievement) => achievement.value === true);

  return (
    <Styled.ProfileWrapper
      ref={wrapperRef}
      isPaidFee={isPaidFee}
      isProfileSizeSmall={isProfileSizeSmall}
      hasDrawer={data?.hasDrawer}
    >
      {isLoading && <SkeletonProfile />}
      {isError && <LoadError refetch={refetch} />}
      {isFetched && !!truckerData && (
        <>
          <ProfileHeader
            onClose={onClose ?? onCloseProp}
            wrapperRef={wrapperRef}
            hasDrawer={data?.hasDrawer}
            isPaidFee={isPaidFee}
          />
          {!truckerData?.isBlocked ? (
            <>
              {isPaidFee && <Deposit grStatus={grStatus} proposalId={proposalId} />}
              {!isPaidFee && <Divider />}
              <GeneralInformation data={truckerData} isOpenSearch={isOpenSearch} />
              <Divider />
              <RiskManagement />
              <Divider />
              <TruckInformation data={truckerData} />
              <SecurityAnalysis data={truckerData} />
              <TravelHistory data={truckerData} />
              <Divider />
              <Rating {...truckerData?.evaluation} />
              {hasAchievement && (
                <>
                  <Divider />
                  <Achievements />
                </>
              )}
            </>
          ) : (
            <BlockedArea iSmallProfile={isProfileSizeSmall} />
          )}
        </>
      )}
      {!hideProfileToasts && <Toasts portalName={portalName} />}
    </Styled.ProfileWrapper>
  );
};

export default Profile;
